import errorAction from 'shared/utils/errorAction';
import isMobile, { isIOS } from 'shared/utils/checkMobileDevice';
import createAsyncAction from 'shared/utils/createAsyncAction';
import { addModal } from 'modules/messageSystem/actions/addModal';

import createAction from './';

const MEDIA_DEVICES_CRITERIA = {
  audio: true,
  video: true,
};

export const ASK_PERMISSION = createAsyncAction(createAction('ASK_PERMISSION'));

function dirtyCheckMobileMediaPermissions() {
  return navigator.mediaDevices.getUserMedia({ audio: true, video: true })
    .then((stream) => {
      console.log("Access granted to camera and microphone");
      stream.getTracks().forEach(track => track.stop());
      return true;
    })
    .catch((error) => {
      if (error.name === "NotAllowedError") {
        console.log("Permissions not granted or denied permanently");
      } else if (error.name === "NotFoundError") {
        console.log("No media devices found");
      } else {
        console.log("Error accessing media devices:", error);
      }

      return false;
    });
}

const checkMediaDevicesAndPermissions = () => {
  const requiredInputs = ['audioinput', 'videoinput'];
  const availableMediaDevices = [];

  return navigator.mediaDevices.enumerateDevices()
    .then((devices) => {
      devices.forEach((device) => {
        availableMediaDevices.push(device.kind);
      });

      const hasAllRequiredInputs = requiredInputs.every(input => availableMediaDevices.includes(input));
      if (!hasAllRequiredInputs) {
        console.error('No required media devices found. Only has:', availableMediaDevices);

        return false;
      }

      return navigator.permissions.query({ name: 'camera' })
        .then(res => {
          if (res.state !== 'granted') return false;

          return navigator.permissions.query({ name: 'microphone' })
            .then(res => {
              return res.state === 'granted';
            });
      });

    })
    .catch((err) => {
      console.log(`${ err.name }: ${ err.message }`);
      return false;
    });
};

export const askVideoPermissionWithDialog = () => (dispatch) => {
  return navigator.mediaDevices.getUserMedia({ video: true })
    .then((stream) => {
      stream.getTracks().forEach(track => track.stop());
      return dispatch({ type: ASK_PERMISSION.success });
    })
    .catch((error) => {
      dispatch(errorAction(ASK_PERMISSION.failure, error));
    });
};

export const askAudioPermissionWithDialog = () => (dispatch) => {
  return navigator.mediaDevices.getUserMedia({ audio: true })
    .then((stream) => {
      stream.getTracks().forEach(track => track.stop());
      return dispatch({ type: ASK_PERMISSION.success });
    })
    .catch((error) => {
      dispatch(errorAction(ASK_PERMISSION.failure, error));
    });
};

const askMobilePermissionWithDialog = () => async (dispatch) => {
  dispatch({ type: ASK_PERMISSION.pending });

  const checkGrated = isIOS()
    ? await dirtyCheckMobileMediaPermissions()
    : await checkMediaDevicesAndPermissions();
  if (checkGrated) return dispatch({ type: ASK_PERMISSION.success });

  return dispatch(askVideoPermissionWithDialog())
    .then(() => dispatch(askAudioPermissionWithDialog()));
};

const askPermissionWithDialog = () => (dispatch) => {
  if (isMobile()) return dispatch(askMobilePermissionWithDialog());

  dispatch({ type: ASK_PERMISSION.pending });

  return Promise.resolve()
    .then(() => {
      if (!navigator.mediaDevices || !navigator.mediaDevices?.getUserMedia) {
        throw new Error('There is no user media in your browser');
      }
      return navigator.mediaDevices.getUserMedia(MEDIA_DEVICES_CRITERIA);
    })
    .then(stream => {
      const tracks = stream.getTracks();
      // In case of disabling only audio or video - its not
      // trigger an error. So we need to cover this case and
      // not allow user to except calls without accessing to one
      // of media source
      if (tracks.length !== 2) {
        dispatch(errorAction(ASK_PERMISSION.failure));
        dispatch(
          addModal({
            heading: 'conference.noPermissionModalHeading',
            body: 'conference.noPermissionModalBody',
          }),
        );
      } else {
        dispatch({ type: ASK_PERMISSION.success });
      }

      return tracks.forEach((t) => t.stop());
    })
    .catch((error) => {
      dispatch(errorAction(ASK_PERMISSION.failure, error));
      if (error.name !== 'NotAllowedError') return;

      dispatch(
        addModal({
          heading: 'conference.noPermissionModalHeading',
          body: 'conference.noPermissionModalBody',
        }),
      );
    });
};


export const askPermission = () => (dispatch) => {
  if (isMobile()) return dispatch(askMobilePermissionWithDialog());

  return Promise.all([
    navigator.permissions.query({ name: 'camera' }),
    navigator.permissions.query({ name: 'microphone' }),
  ])
    .then(([permissionCamStatus, permissionMicStatus]) => {

      if (permissionCamStatus.state === 'granted' && permissionMicStatus.state === 'granted') {
        return dispatch({ type: ASK_PERMISSION.success });
      }

      switch (true) {
        case (permissionMicStatus.state !== 'granted' && permissionCamStatus.state !== 'granted'):
          return dispatch(askPermissionWithDialog());

        case (permissionMicStatus.state !== 'granted'):
          return dispatch(askAudioPermissionWithDialog());

        case (permissionCamStatus.state !== 'granted'):
          return dispatch(askVideoPermissionWithDialog());

        default: return null;
      }
    });
};
